import { useNavigate } from "react-router-dom";
import { EditUserForm, Loader } from "../../../components";
import { useID } from "../../../hooks";
import i18next from "../../../i18n";
import {
  useAssignUserRoles,
  useUpdateUser,
  useUserById,
  useUsersByCompany,
} from "../../../hooks/useUsers";
import { useNotification } from "../../../context";

const EditUser = () => {
  const { openNotification } = useNotification();
  const id = useID();
  const navigate = useNavigate();

  const { data: userToEdit, isLoading: isLoadingUser } = useUserById(id);

  const { data: usersByCompany, isLoading: isLoadingUsers } =
    useUsersByCompany(id);

  // `mutateAsync` so the roles assignment can be sequenced AFTER the user PUT succeeds. The
  // hook-level onSuccess is a no-op: success feedback + navigation happen in `handleEditUser`
  // once the (optional) role assignment has settled too. `isError` still drives the form's
  // error banner; a rejected PUT is swallowed here (onError already toasted).
  const {
    mutateAsync: editUserAsync,
    isPending: isUpdatingUser,
    isError,
  } = useUpdateUser(
    () => {},
    () => openNotification(i18next.t("newMaterialForm.error"))
  );

  const { mutateAsync: assignUserRolesAsync, isPending: isAssigningRoles } =
    useAssignUserRoles();

  const handleEditUser = async ({
    userId,
    body,
    roleIds,
  }: {
    userId: string;
    body: any;
    roleIds?: string[];
  }) => {
    try {
      await editUserAsync({ userId, body });
    } catch {
      return;
    }
    // The user is already updated, so a failed role assignment must not read as a failed edit:
    // warn distinctly and still leave the form.
    let rolesFailed = false;
    if (roleIds) {
      try {
        await assignUserRolesAsync({ id: userId, roleIds });
      } catch {
        rolesFailed = true;
      }
    }
    openNotification(
      i18next.t(rolesFailed ? "users.editedRolesFailed" : "users.editSuccess")
    );
    navigate("/users");
  };

  if (isLoadingUser || isLoadingUsers) return <Loader isFullScreen />;

  return (
    <>
      <EditUserForm
        error={isError}
        userToEdit={userToEdit}
        onEditUser={handleEditUser}
        usersByCompany={usersByCompany}
        // Threaded through to ConfirmDialog rather than gating a full-screen loader here: the
        // pre-Carbon version unmounted the whole form (dialog included) the instant the mutation
        // started, so the confirm dialog itself never got to show its own pending state. This
        // matches how NewUserForm.tsx (Task 11) already handles its own create mutation.
        isPending={isUpdatingUser || isAssigningRoles}
      />
    </>
  );
};

export default EditUser;
